import { useCallback, useState, type MouseEvent } from "react";

interface DeleteButtonProps {
  onDelete: () => Promise<void> | void;
  confirmMessage?: string;
  label?: string;
  size?: "sm" | "md";
  className?: string;
  stopPropagation?: boolean;
  isLoading?: boolean;
  errorMessage?: string | null;
  onClearError?: () => void;
}

const DEFAULT_CONFIRM_MESSAGE = "Delete this item? This action cannot be undone.";

export function DeleteButton({
  onDelete,
  confirmMessage,
  label = "Delete",
  size = "md",
  className,
  stopPropagation = true,
  isLoading,
  errorMessage,
  onClearError,
}: DeleteButtonProps): JSX.Element {
  const [internalError, setInternalError] = useState<string | null>(null);
  const [internalLoading, setInternalLoading] = useState<boolean>(false);

  const effectiveLoading = isLoading ?? internalLoading;
  const effectiveError = errorMessage ?? internalError;

  const handleDelete = useCallback(
    async (event: MouseEvent<HTMLButtonElement>) => {
      if (stopPropagation) {
        event.preventDefault();
        event.stopPropagation();
      }

      const confirmed = window.confirm(confirmMessage ?? DEFAULT_CONFIRM_MESSAGE);
      if (!confirmed) return;

      onClearError?.();
      setInternalError(null);

      if (isLoading === undefined) {
        setInternalLoading(true);
      }

      try {
        await onDelete();
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to delete item";
        setInternalError(message);
      } finally {
        if (isLoading === undefined) {
          setInternalLoading(false);
        }
      }
    },
    [confirmMessage, isLoading, onClearError, onDelete, stopPropagation],
  );

  const sizeClass = size === "sm" ? "button-sm" : "";

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        className={`button button-secondary button-danger ${sizeClass} ${className ?? ""}`.trim()}
        onClick={handleDelete}
        disabled={effectiveLoading}
      >
        {effectiveLoading ? "Deleting…" : label}
      </button>
      {effectiveError ? <span className="text-xs text-red-600">{effectiveError}</span> : null}
    </div>
  );
}
